import React, { useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BeachAccess as VacationIcon,
  Close as CloseIcon,
  EventBusy as DayOffIcon,
  Groups as ActiveIcon,
  EventAvailable as NotScheduledIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  formatRosterAgentLabel,
  formatVacationAgentLabel,
  formatVacationDateRange,
  RosterAgentItem,
  RosterMetrics,
} from '../../utils/rosterMetrics';

interface RosterInsightsPanelProps {
  metrics: RosterMetrics;
  compact?: boolean;
}

type RosterDialogKey = 'dayOff' | 'notScheduled';

interface MetricTileProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  accent: string;
  prominent?: boolean;
  tooltip?: string;
  onClick?: () => void;
}

const MetricTile: React.FC<MetricTileProps> = ({
  icon,
  label,
  value,
  accent,
  prominent = false,
  tooltip,
  onClick,
}) => {
  const clickable = Boolean(onClick);
  const tile = (
    <Box
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      } : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: prominent ? 1.75 : 1.25,
        py: prominent ? 1 : 0.75,
        borderRadius: 999,
        bgcolor: prominent ? '#ffffff' : 'rgba(255,255,255,0.12)',
        border: prominent ? '1px solid rgba(255,255,255,0.9)' : '1px solid rgba(255,255,255,0.18)',
        boxShadow: prominent ? '0 2px 8px rgba(0,0,0,0.12)' : 'none',
        minWidth: 0,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease, transform 0.15s ease',
        '&:hover': clickable ? {
          bgcolor: prominent ? '#ffffff' : 'rgba(255,255,255,0.2)',
          transform: 'translateY(-1px)',
        } : undefined,
      }}
    >
      <Box sx={{ color: prominent ? accent : '#ffffff', display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: prominent ? '1.25rem' : '1rem',
            fontWeight: 700,
            lineHeight: 1.1,
            color: prominent ? accent : '#ffffff',
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.65rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: prominent ? 'text.secondary' : 'rgba(255,255,255,0.82)',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );

  const tip = [
    tooltip,
    clickable ? 'Click to view agent list.' : null,
  ].filter(Boolean).join(' ');

  if (!tip) return tile;
  return (
    <Tooltip title={tip} arrow placement="top">
      <Box>{tile}</Box>
    </Tooltip>
  );
};

interface RosterAgentListDialogProps {
  open: boolean;
  title: string;
  agents: RosterAgentItem[];
  emptyMessage: string;
  onClose: () => void;
  subtitle?: string;
}

const RosterAgentListDialog: React.FC<RosterAgentListDialogProps> = ({
  open,
  title,
  agents,
  emptyMessage,
  onClose,
  subtitle,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ pr: 6, pb: 1 }}>
      {title}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {subtitle || `${agents.length} agent${agents.length === 1 ? '' : 's'}`}
      </Typography>
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 12, top: 12 }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ pt: 0 }}>
      {agents.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {emptyMessage}
        </Typography>
      ) : (
        <List dense disablePadding>
          {agents.map((agent, index) => (
            <ListItem
              key={agent.userId}
              divider={index < agents.length - 1}
              sx={{ px: 0, alignItems: 'flex-start' }}
            >
              <ListItemText
                primary={(
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography component="span" fontWeight={600} fontSize="0.95rem">
                      {formatRosterAgentLabel(agent)}
                    </Typography>
                    {agent.clockedIn && (
                      <Chip
                        label="Clocked in"
                        size="small"
                        color="success"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                      />
                    )}
                  </Stack>
                )}
                secondary={
                  agent.clockedIn
                    ? `No shift scheduled · clocked in at ${agent.clockInTime || '—'}${agent.clockInCampaignName ? ` (${agent.clockInCampaignName})` : ''}`
                    : agent.campaignName === 'Unassigned'
                      ? 'No campaign assigned today'
                      : 'No shift scheduled today'
                }
                secondaryTypographyProps={{ sx: { mt: 0.35 } }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </DialogContent>
  </Dialog>
);

const RosterInsightsPanel: React.FC<RosterInsightsPanelProps> = ({ metrics, compact = false }) => {
  const [vacationOpen, setVacationOpen] = useState(metrics.vacationAgents.length > 0 && !compact);
  const [dialogKey, setDialogKey] = useState<RosterDialogKey | null>(null);

  const vacationCount = metrics.vacationAgents.length;
  const notScheduledAgents = metrics.notScheduledAgents ?? [];
  const notScheduledClockedInCount = notScheduledAgents.filter((agent) => agent.clockedIn).length;
  const dialogConfig = dialogKey === 'dayOff'
    ? {
      title: 'Day Off',
      agents: metrics.dayOffAgents ?? [],
      emptyMessage: 'No approved day-off absences for today.',
      subtitle: undefined as string | undefined,
    }
    : dialogKey === 'notScheduled'
      ? {
        title: 'Not Scheduled',
        agents: notScheduledAgents,
        emptyMessage: 'No active employees without a planned shift today.',
        subtitle: notScheduledClockedInCount > 0
          ? `${metrics.notScheduled} awaiting clock-in · ${notScheduledClockedInCount} clocked in without a shift (not counted above)`
          : `${metrics.notScheduled} agent${metrics.notScheduled === 1 ? '' : 's'} awaiting clock-in`,
      }
      : null;

  return (
    <>
      <Box
        sx={{
          mb: compact ? 0.75 : 1.5,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 55%, #3949ab 100%)',
          color: '#ffffff',
          px: compact ? 1.25 : 2,
          py: compact ? 1 : 1.5,
          boxShadow: '0 6px 20px rgba(26, 35, 126, 0.22)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ mb: compact ? 0.75 : 1 }}
        >
          <Typography
            variant="caption"
            sx={{
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              fontWeight: 700,
              color: 'rgba(255,255,255,0.88)',
              fontSize: compact ? '0.62rem' : '0.68rem',
            }}
          >
            Workforce Roster
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.65)',
              fontSize: compact ? '0.58rem' : '0.65rem',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Same metrics as Daily Staffing Alert
          </Typography>
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          spacing={compact ? 0.75 : 1}
          alignItems="center"
        >
          <MetricTile
            icon={<ActiveIcon sx={{ fontSize: compact ? 18 : 22 }} />}
            label="Active Employees"
            value={metrics.activeAgentCount}
            accent="#1a237e"
            prominent
            tooltip="Active operational roster excluding super users and system accounts."
          />
          <MetricTile
            icon={<DayOffIcon sx={{ fontSize: compact ? 16 : 20 }} />}
            label="Day Off"
            value={metrics.dayOff}
            accent="#a5d6a7"
            tooltip="Approved manual day-off absences for today."
            onClick={metrics.dayOff > 0 ? () => setDialogKey('dayOff') : undefined}
          />
          <MetricTile
            icon={<NotScheduledIcon sx={{ fontSize: compact ? 16 : 20 }} />}
            label="Not Scheduled"
            value={metrics.notScheduled}
            accent="#cfd8dc"
            tooltip="Employees with no planned shift who have not clocked in. Open the list to also see clocked-in agents working without a schedule."
            onClick={notScheduledAgents.length > 0 ? () => setDialogKey('notScheduled') : undefined}
          />
          <MetricTile
            icon={<VacationIcon sx={{ fontSize: compact ? 16 : 20 }} />}
            label="On Vacation"
            value={vacationCount}
            accent="#e1bee7"
            tooltip={
              vacationCount > 0
                ? 'Approved PTO / vacation where today falls in the absence range.'
                : 'No approved vacation absences for today.'
            }
          />
          {vacationCount > 0 && (
            <IconButton
              size="small"
              onClick={() => setVacationOpen((open) => !open)}
              aria-label={vacationOpen ? 'Hide vacation list' : 'Show vacation list'}
              sx={{
                color: '#ffffff',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                transform: vacationOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.2s ease',
              }}
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        <Collapse in={vacationOpen && vacationCount > 0}>
          <Box
            sx={{
              mt: 1.25,
              pt: 1.25,
              borderTop: '1px solid rgba(255,255,255,0.16)',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.85)',
                mb: 0.75,
                fontSize: '0.65rem',
              }}
            >
              On Vacation Today
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
              {metrics.vacationAgents.map((agent) => (
                <Tooltip
                  key={agent.userId}
                  title={`${formatVacationDateRange(agent)} · Approved vacation`}
                  arrow
                  placement="top"
                >
                  <Chip
                    label={formatVacationAgentLabel(agent)}
                    size="small"
                    sx={{
                      height: compact ? 24 : 28,
                      fontWeight: 600,
                      fontSize: compact ? '0.68rem' : '0.72rem',
                      bgcolor: 'rgba(255,255,255,0.95)',
                      color: '#6a1b9a',
                      border: '1px solid rgba(106, 27, 154, 0.2)',
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Box>
        </Collapse>
      </Box>

      {dialogConfig && (
        <RosterAgentListDialog
          open={Boolean(dialogKey)}
          title={dialogConfig.title}
          agents={dialogConfig.agents}
          emptyMessage={dialogConfig.emptyMessage}
          subtitle={dialogConfig.subtitle}
          onClose={() => setDialogKey(null)}
        />
      )}
    </>
  );
};

export default RosterInsightsPanel;
